import React, { useEffect, useMemo, useState } from 'react'
import { Grid, TextField, Typography, Stack } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import Card from '../../shared/layout/Card'
import Button from '../../shared/UI/Button'
import { ManAuthRequestResponse } from '../../types/ManAuthRequests'
import { RequestStatus } from '../OccurrenceRequest/types'
import { manualRequestsService } from '../../services/myActionsService'
import NumberInput from '../../shared/UI/NumberInput'

interface EditCardProps {
  isManager: boolean | null | undefined
  hours: number | null
  setHours: (value: number | null) => void
  comments: string
  setComments: (value: string) => void
  approverComments: string
  setApproverComments: (value: string) => void
  maxAllowedHours: number
  isHoursChanged: boolean
  areFieldsChanged: boolean
  isSubmitting: boolean
  manAuthRequest: ManAuthRequestResponse | null
  handleRequestUpdate: (requestStatusId: number) => Promise<void>
  hasValidRtw: boolean
}

export default function EditCard({
  isManager,
  hours,
  setHours,
  comments,
  setComments,
  approverComments,
  setApproverComments,
  maxAllowedHours,
  isHoursChanged,
  areFieldsChanged,
  isSubmitting,
  manAuthRequest,
  handleRequestUpdate,
  hasValidRtw,
}: EditCardProps) {
  const [initialHours, setInitialHours] = useState<number>(0)
  const [hoursValidationError, setHoursValidationError] = useState(false)
  const [initialComments, setInitialComments] = useState<string>('')
  const navigate = useNavigate()

  const [hasDeclined, setHasDeclined] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [pendingAction, setPendingAction] = useState<'submit' | 'approve' | 'decline' | null>(null)
  const isRequestApproved = () => manAuthRequest?.requestStatus === 'Approved'
  const isRequestAmended = () => manAuthRequest?.requestStatus === 'Amended'
  const isRequestDeclined = () => manAuthRequest?.requestStatus === 'Declined'
  const managerCommentDisabled = !isManager || isRequestApproved() || isRequestAmended() || isRequestDeclined()

  useEffect(() => {
    if (hours) setInitialHours(hours)
    setInitialComments(comments)
  }, [])

  const hasRequestChanged = useMemo(
    () => hours !== initialHours || comments !== initialComments,
    [hours, initialHours, comments, initialComments]
  )

  const isSubmitWithChangesDisabled = () => {
    if (isManager) {
      return !hasRequestChanged
    }

    return (
      (manAuthRequest?.requestStatus !== 'Pending' && !isHoursChanged) ||
      isSubmitting ||
      isRequestApproved() ||
      isRequestDeclined()
    )
  }

  const showApproverCommentError = hasDeclined && approverComments.trim().length === 0
  const areManagerCommentsEmpty = comments.trim().length === 0
  const showManagerCommentError = hasSubmitted && areManagerCommentsEmpty
  const isDeclineButtonDisabled =
    isSubmitting ||
    manAuthRequest?.requestStatus === 'Declined' ||
    hasValidRtw

  const isApproveButtonDisabled =
    isSubmitting ||
    hasValidRtw ||
    ((manAuthRequest?.requestStatus === 'Approved' ||
      manAuthRequest?.requestStatus === 'Amended') &&
      !areFieldsChanged)

  const isHoursInputDisabled = (managerCommentDisabled && !!isManager) || hasValidRtw
  const areApproverCommentsDisabled = hasValidRtw

  const handleHoursChange = (newHours: number | null) => {
    setHours(newHours)
    // Don't clear validation error here - it should only clear when submit button is pressed
  }

  const validateHours = () => {
    if (hours === null || hours === undefined) {
      setHoursValidationError(true)
      return false
    }
    setHoursValidationError(false)
    return true
  }

  const handleCommentsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setComments(e.target.value)
  }

  const handleApproverCommentsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setApproverComments(e.target.value)
  }
  const handleCancel = async () => {
    if (manAuthRequest?.id) {
      await manualRequestsService
        .DeleteManAuthRequest({
          ManAuthRequestId: manAuthRequest.id,
        })
        .then(() => navigate('/myactions'))
        .catch(() => alert('Error deleting discretionary pay request, please try again'))
    }
  }

  const handleSubmitWithChanges = async () => {
    setHasSubmitted(true)
    if (!validateHours() || areManagerCommentsEmpty) {
      return
    }
    setHoursValidationError(false)
    setPendingAction('submit')
    try {
      await handleRequestUpdate(RequestStatus.PENDING)
    } finally {
      setPendingAction(null)
    }
  }
  const handleApprove = async () => {
    if (!validateHours()) {
      return
    }
    setHoursValidationError(false)
    setPendingAction('approve')
    try {
      await handleRequestUpdate(areFieldsChanged ? RequestStatus.AMENDED : RequestStatus.APPROVED)
    } finally {
      setPendingAction(null)
    }
  }

  const handleDecline = async () => {
    setHasDeclined(true)
    if (!validateHours()) {
      return
    }
    if (!approverComments.trim()) {
      return
    }
    setHoursValidationError(false)
    setPendingAction('decline')
    try {
      await handleRequestUpdate(5)
    } finally {
      setPendingAction(null)
    }
  }

  const cardTitle = isManager ? 'Edit' : 'Approval'

  return (
    <Card title={cardTitle} noDivider>
      <Grid container spacing={3} mt={0}>
        {isManager ? (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Comments"
              multiline
              rows={4}
              placeholder="Placeholder"
              value={comments}
              onChange={handleCommentsChange}
              error={showManagerCommentError}
              variant="outlined"
              size="small"
              disabled={managerCommentDisabled}
            />
          </Grid>
        ) : (
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manager's Comments"
                multiline
                rows={4}
                placeholder="Placeholder"
                value={comments}
                onChange={handleCommentsChange}
                variant="outlined"
                size="small"
                disabled={managerCommentDisabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                data-testid="approver-comments"
                label="Comments"
                multiline
                rows={4}
                placeholder="Placeholder"
                value={approverComments}
                onChange={handleApproverCommentsChange}
                variant="outlined"
                size="small"
                error={showApproverCommentError}
                disabled={areApproverCommentsDisabled}
              />
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <NumberInput
            fullWidth
            label="Hours"
            value={hours}
            showMaxValue
            onChange={handleHoursChange}
            variant="outlined"
            size="small"
            max={maxAllowedHours}
            disabled={isHoursInputDisabled}
            error={hoursValidationError}
            helperText={hoursValidationError ? 'This field cannot be left blank' : 'Required'}
          />
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="space-between">
            {isManager ? (
              <>
                <Button
                  label="Cancel Request"
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={managerCommentDisabled}
                />
                <Button
                  label={areFieldsChanged ? 'Submit with changes' : 'Submit'}
                  variant="outlined"
                  color="primary"
                  onClick={handleSubmitWithChanges}
                  loading={pendingAction === 'submit'}
                  disabled={isSubmitWithChangesDisabled()}
                />
              </>
            ) : (
              <>
                <Button
                  label="Decline"
                  variant="outlined"
                  color="secondary"
                  onClick={handleDecline}
                  loading={pendingAction === 'decline'}
                  disabled={isDeclineButtonDisabled}
                  dataTestId="decline-button"
                />
                <Button
                  label={areFieldsChanged ? 'Approve with changes' : 'Approve'}
                  variant="outlined"
                  color="primary"
                  onClick={handleApprove}
                  loading={pendingAction === 'approve'}
                  disabled={isApproveButtonDisabled}
                  dataTestId="approve-button"
                />
              </>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  )
}
